import { cn } from '@rocco/utils/cn';

export interface TeamMember {
    id: number;
    name: string;
    jobTitle: string;
    image: string;
}

export interface TeamGridProps {
    members: TeamMember[];
    className?: string;
    /** When set, tiles render as buttons (e.g. opening the member modal). */
    onMemberClick?: (member: TeamMember) => void;
}

const TeamMemberTileBody = ({ member }: { member: TeamMember }) => (
    <>
        <div className="bg-neutral-200 relative aspect-[437/524] w-full overflow-hidden rounded-tile">
            {member.image ? (
                <img
                    src={member.image}
                    alt={member.name}
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            ) : null}
        </div>
        <p className="m-0 font-sans leading-snug font-medium text-[#181818] max-md:pt-3 max-md:text-base md:pt-[0.625rem] md:text-[1.375rem]">
            {member.name}
        </p>
        {member.jobTitle ? (
            <p className="m-0 font-sans leading-snug text-[#181818]/40 max-md:text-sm md:text-[1.375rem]">
                {member.jobTitle}
            </p>
        ) : null}
    </>
);

/**
 * Grid of team-member tiles (portrait photo + name + role) — the professional
 * "(02) Team" section. Two columns on mobile, four on desktop. Self-hides when
 * empty.
 */
export const TeamGrid = ({
    members,
    className,
    onMemberClick,
}: TeamGridProps) => {
    if (members.length === 0) return null;

    return (
        <div
            className={cn(
                'grid grid-cols-2 max-md:gap-x-2 max-md:gap-y-8 md:grid-cols-4 md:gap-x-[1.5rem] md:gap-y-[2.4rem]',
                className,
            )}
        >
            {members.map((member) =>
                onMemberClick ? (
                    <button
                        key={member.id}
                        type="button"
                        onClick={() => onMemberClick(member)}
                        className="flex cursor-pointer flex-col text-left"
                    >
                        <TeamMemberTileBody member={member} />
                    </button>
                ) : (
                    <div key={member.id} className="flex flex-col">
                        <TeamMemberTileBody member={member} />
                    </div>
                ),
            )}
        </div>
    );
};
